import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Dimensions,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRide } from '../context/RideContext';

const { width, height } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const ACCEPT_TIMEOUT_SECONDS = 30;

const IncomingRideModal = ({ onAccepted }) => {
  const { pendingRequests, acceptRide, declineRide, isOnline } = useRide();
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(height)).current;
  const [accepting, setAccepting] = useState(null);
  const [countdown, setCountdown] = useState(ACCEPT_TIMEOUT_SECONDS);
  const countdownRef = useRef(null);

  const ride = pendingRequests.length > 0 ? pendingRequests[0] : null;

  useEffect(() => {
    if (ride) {
      // Slide in
      Animated.spring(slideAnim, {
        toValue: 0,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }).start();

      // Pulse animation for fare
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.08,
            duration: 600,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 600,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      // Slide out
      Animated.timing(slideAnim, {
        toValue: height,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [ride]);

  // Countdown timer — auto-decline after 30s
  useEffect(() => {
    if (ride) {
      setCountdown(ACCEPT_TIMEOUT_SECONDS);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            declineRide(ride.id);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [ride?.id]);

  if (!ride || !isOnline) return null;

  const handleAccept = async () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setAccepting(ride.id);
    const result = await acceptRide(ride.id);
    setAccepting(null);
    if (result.success) {
      onAccepted && onAccepted(result.ride);
    } else {
      if (isWeb) window.alert(result.message || 'Ride is no longer available');
    }
  };

  const handleDecline = () => {
    declineRide(ride.id);
  };

  const fare = ride.estimatedFare || 0;
  const distance = ride.distance || 'N/A';
  const riderName = ride.rider?.name || 'Rider';

  const getTimeAgo = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const diffMin = Math.floor((new Date() - d) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin === 1) return '1 min ago';
    return `${diffMin} min ago`;
  };

  return (
    <Animated.View
      style={[
        styles.overlay,
        { transform: [{ translateY: slideAnim }] },
      ]}
    >
      <LinearGradient
        colors={['#000000', '#0a0a1a', '#111122']}
        style={styles.container}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerIconRow}>
            <Animated.View style={[styles.bellContainer, { transform: [{ scale: pulseAnim }] }]}>
              <Ionicons name="notifications" size={32} color="#D4AF37" />
            </Animated.View>
          </View>
          <Text style={styles.headerTitle}>INCOMING RIDE REQUEST</Text>
          <Text style={[styles.headerSubtitle, {
            color: countdown > 10 ? 'rgba(255,255,255,0.5)' : countdown > 5 ? '#FF9800' : '#F44336'
          }]}>
            {countdown}s remaining to accept
          </Text>
          <View style={styles.countdownBar}>
            <View style={[
              styles.countdownBarFill,
              {
                width: `${(countdown / ACCEPT_TIMEOUT_SECONDS) * 100}%`,
                backgroundColor: countdown > 10 ? '#4CAF50' : countdown > 5 ? '#FF9800' : '#F44336',
              }
            ]} />
          </View>
        </View>

        {/* Fare */}
        <Animated.View style={[styles.fareContainer, { transform: [{ scale: pulseAnim }] }]}>
          <Text style={styles.fareLabel}>ESTIMATED FARE</Text>
          <Text style={styles.fareAmount}>${fare.toFixed(2)}</Text>
        </Animated.View>

        {/* Rider Info */}
        <View style={styles.riderRow}>
          <View style={styles.riderAvatar}>
            <Ionicons name="person" size={24} color="#D4AF37" />
          </View>
          <View style={styles.riderInfo}>
            <Text style={styles.riderName}>{riderName}</Text>
            <Text style={styles.rideType}>{ride.type || 'STANDARD'}</Text>
          </View>
          <View style={styles.distanceBadge}>
            <Text style={styles.distanceText}>
              {typeof distance === 'number' ? `${distance} mi` : distance}
            </Text>
          </View>
        </View>

        {/* Route */}
        <View style={styles.routeContainer}>
          <View style={styles.routeRow}>
            <View style={styles.routeDotContainer}>
              <View style={styles.pickupDot} />
              <View style={styles.routeLine} />
            </View>
            <View style={styles.routeTextContainer}>
              <Text style={styles.routeLabel}>PICKUP</Text>
              <Text style={styles.routeAddress} numberOfLines={2}>{ride.origin}</Text>
            </View>
          </View>
          <View style={styles.routeRow}>
            <View style={styles.routeDotContainer}>
              <View style={styles.destDot} />
            </View>
            <View style={styles.routeTextContainer}>
              <Text style={styles.routeLabel}>DESTINATION</Text>
              <Text style={styles.routeAddress} numberOfLines={2}>{ride.destination}</Text>
            </View>
          </View>
        </View>

        {/* Pending count */}
        {pendingRequests.length > 1 && (
          <Text style={styles.pendingCount}>
            +{pendingRequests.length - 1} more request{pendingRequests.length > 2 ? 's' : ''} waiting
          </Text>
        )}

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={styles.declineButton}
            onPress={handleDecline}
            disabled={!!accepting}
          >
            <Ionicons name="close-circle" size={28} color="#FF4444" />
            <Text style={styles.declineText}>DECLINE</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.acceptButton, !!accepting && styles.acceptButtonDisabled]}
            onPress={handleAccept}
            disabled={!!accepting}
          >
            <LinearGradient
              colors={accepting ? ['#666', '#444'] : ['#4CAF50', '#2E7D32']}
              style={styles.acceptGradient}
            >
              {accepting ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <>
                  <Ionicons name="checkmark-circle" size={28} color="#fff" />
                  <Text style={styles.acceptText}>ACCEPT</Text>
                </>
              )}
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 9999,
  },
  container: {
    flex: 1,
    paddingTop: 60,
    paddingHorizontal: 24,
    paddingBottom: 40,
    justifyContent: 'space-between',
  },
  header: {
    alignItems: 'center',
    marginBottom: 16,
  },
  headerIconRow: {
    marginBottom: 12,
  },
  bellContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(212, 175, 55, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(212, 175, 55, 0.3)',
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#D4AF37',
    letterSpacing: 2,
    marginTop: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    marginTop: 4,
    fontWeight: '600',
  },
  countdownBar: {
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    marginTop: 12,
    marginHorizontal: 40,
    overflow: 'hidden',
    width: width - 128,
  },
  countdownBarFill: {
    height: '100%',
    borderRadius: 2,
  },
  fareContainer: {
    alignItems: 'center',
    paddingVertical: 20,
    marginVertical: 8,
    backgroundColor: 'rgba(212, 175, 55, 0.08)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(212, 175, 55, 0.2)',
  },
  fareLabel: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.6)',
    letterSpacing: 2,
    fontWeight: '600',
    marginBottom: 4,
  },
  fareAmount: {
    fontSize: 52,
    fontWeight: '900',
    color: '#4CAF50',
  },
  riderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  riderAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(212, 175, 55, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  riderInfo: {
    flex: 1,
  },
  riderName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
  },
  rideType: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
    marginTop: 2,
    fontWeight: '500',
  },
  distanceBadge: {
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(33, 150, 243, 0.3)',
  },
  distanceText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#2196F3',
  },
  routeContainer: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  routeRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  routeDotContainer: {
    width: 24,
    alignItems: 'center',
    marginRight: 12,
    paddingTop: 4,
  },
  pickupDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#4CAF50',
    borderWidth: 2,
    borderColor: '#81C784',
  },
  destDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#F44336',
    borderWidth: 2,
    borderColor: '#E57373',
  },
  routeLine: {
    width: 2,
    height: 24,
    backgroundColor: 'rgba(255,255,255,0.15)',
    marginTop: 4,
  },
  routeTextContainer: {
    flex: 1,
  },
  routeLabel: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 4,
  },
  routeAddress: {
    fontSize: 15,
    color: '#ffffff',
    lineHeight: 20,
  },
  pendingCount: {
    textAlign: 'center',
    fontSize: 13,
    color: '#D4AF37',
    marginBottom: 8,
    fontWeight: '600',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  declineButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#FF4444',
    backgroundColor: 'rgba(255, 68, 68, 0.08)',
    gap: 8,
  },
  declineText: {
    fontSize: 17,
    fontWeight: '800',
    color: '#FF4444',
    letterSpacing: 1,
  },
  acceptButton: {
    flex: 1.5,
    borderRadius: 16,
    overflow: 'hidden',
  },
  acceptButtonDisabled: {
    opacity: 0.7,
  },
  acceptGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    gap: 8,
  },
  acceptText: {
    fontSize: 17,
    fontWeight: '800',
    color: '#ffffff',
    letterSpacing: 1,
  },
});

export default IncomingRideModal;
